import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ScheduleOnEndInput, type ProductSchedule } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import { Label } from '@nucleus-commerce/ui/components/label';
import { RadioGroup, RadioGroupItem } from '@nucleus-commerce/ui/components/radio-group';

import { useEndSchedule } from '../hooks/use-schedule-mutations';

import { ScheduleErrorAlert } from './schedule-error-alert';

export interface EndScheduleDialogProps {
  schedule: ProductSchedule;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The release run the end ran, to open it; null when nothing was live. */
  onEnded?: (releaseRunId: string | null) => void;
}

/**
 * Ends the schedule now. The choice overrides the stored `onEnd` for this end only: unpublish
 * what the schedule has live, or leave it published. Store products stay in every store.
 */
export function EndScheduleDialog({
  schedule,
  open,
  onOpenChange,
  onEnded,
}: EndScheduleDialogProps) {
  const { t } = useTranslation(['schedules', 'common']);
  const end = useEndSchedule(schedule.id);
  const [onEnd, setOnEnd] = useState<ScheduleOnEndInput>(schedule.onEnd);
  // Per-store counts: a product live in two stores is two listings, so they are not products.
  const live = schedule.stores.reduce((sum, store) => sum + store.placements.live, 0);
  const liveStores = schedule.stores.filter((store) => store.placements.live > 0).length;
  /** Cancel, Escape and a finished end all go here, so no old error comes back. */
  const close = () => {
    end.reset();
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
        else onOpenChange(true);
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('schedules:end.title', { name: schedule.name })}</DialogTitle>
          <DialogDescription>
            {t('schedules:end.description', {
              count: live,
              stores: t('schedules:detail.stores', { count: liveStores }),
            })}
          </DialogDescription>
        </DialogHeader>
        {end.isError ? <ScheduleErrorAlert error={end.error} action="end" /> : null}
        <RadioGroup
          value={onEnd}
          onValueChange={(value) => setOnEnd(value as ScheduleOnEndInput)}
          className="gap-3"
          aria-label={t('schedules:fields.onEnd')}
        >
          {Object.values(ScheduleOnEndInput).map((value) => (
            <div key={value} className="flex items-start gap-2.5">
              <RadioGroupItem value={value} id={`end-on-end-${value}`} className="mt-0.5" />
              <div className="grid gap-0.5">
                <Label htmlFor={`end-on-end-${value}`}>{t(`schedules:onEnd.${value}`)}</Label>
                <span className="text-muted-foreground text-xs">
                  {t(`schedules:onEndHint.${value}`)}
                </span>
              </div>
            </div>
          ))}
        </RadioGroup>
        <p className="text-muted-foreground text-xs">{t('schedules:end.final')}</p>
        <DialogFooter>
          <Button type="button" variant="outline" disabled={end.isPending} onClick={close}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={end.isPending}
            onClick={() =>
              end.mutate(onEnd, {
                onSuccess: (change) => {
                  close();
                  onEnded?.(change.releaseRunId);
                },
              })
            }
          >
            {end.isPending ? t('schedules:end.ending') : t('schedules:end.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
